import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { conversionRecommendationsService } from '@/services/conversionRecommendations.service';
import {
  isConversionRecommendationDismissed,
  markConversionRecommendationDismissed,
  resolveRecommendationTiming,
  selectRecommendationToDisplay,
  type ConversionRecommendation,
  type ConversionRecommendationElement,
} from '@/lib/conversionRecommendations';

// Le tuto prime quand il existe : sinon on renvoie vers l'écran de configuration.
type ElementConfig = {
  image: string;
  route: (webinarId: number) => string;
  tutorialUrl?: string;
};

// Les notifications d'achat se configurent dans l'offre, donc dans le même onglet qu'elle.
const ELEMENT_CONFIG: Record<ConversionRecommendationElement, ElementConfig> = {
  LIVE_OFFER: {
    image: '/assets/conversion-reco-offer.webp',
    route: (webinarId) => `/webinars/${webinarId}?tab=live&focus=offers`,
  },
  PURCHASE_NOTIFICATIONS: {
    image: '/assets/conversion-reco-notifications.webp',
    route: (webinarId) => `/webinars/${webinarId}?tab=live&focus=offers`,
  },
  CONVERSION_LABEL: {
    image: '/assets/conversion-reco-label.webp',
    route: (webinarId) => `/webinars/${webinarId}?tab=interactions`,
    tutorialUrl: 'https://youtu.be/IHL28HUxXrY',
  },
  THANK_YOU_PAGE_OFFER: {
    image: '/assets/conversion-reco-thankyou.webp',
    route: (webinarId) => `/webinars/${webinarId}?tab=thank-you`,
    tutorialUrl: 'https://youtu.be/aFMB04ULuzE',
  },
};

function VideoIcon() {
  return <img src="/assets/conversion-reco-video-play.svg" alt="" className="size-6 shrink-0" />;
}

function toStringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

export default function ConversionRecommendationModal() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const workspaceId = useAppSelector((s: RootState) => s.workspace.currentWorkspace?.id);

  const [recommendation, setRecommendation] = useState<ConversionRecommendation | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isDisabledPreference, setIsDisabledPreference] = useState(false);

  useEffect(() => {
    // Remise à zéro avant l'appel : sans elle, la recommandation de l'espace précédent reste
    // affichée pendant le chargement, et définitivement si l'appel échoue, avec un CTA qui
    // pointe vers un webinaire d'un autre espace.
    setRecommendation(null);
    setIsOpen(false);
    setIsDisabledPreference(false);

    if (!workspaceId) return;

    let isCancelled = false;
    conversionRecommendationsService
      .list(workspaceId)
      .then((recommendations) => {
        if (isCancelled) return;
        const next = selectRecommendationToDisplay(
          recommendations,
          isConversionRecommendationDismissed,
        );
        setRecommendation(next);
        setIsOpen(next !== null);
      })
      .catch(() => {
        // rappel non bloquant : en cas d'échec on n'affiche simplement rien
      });

    return () => {
      isCancelled = true;
    };
  }, [workspaceId]);

  if (!recommendation || !workspaceId) return null;

  const missingElements = recommendation.missingElements;
  const isSingleElement = missingElements.length === 1;
  const timing = resolveRecommendationTiming(recommendation.plannedDate);

  const close = () => {
    markConversionRecommendationDismissed(recommendation.id);
    setIsOpen(false);
  };

  const handlePreferenceChange = (checked: boolean) => {
    setIsDisabledPreference(checked);
    conversionRecommendationsService.updatePreference(workspaceId, checked).catch(() => {
      setIsDisabledPreference(!checked);
      toast.error(t('conversionRecommendations.preferenceError'));
    });
  };

  const renderCta = (element: ConversionRecommendationElement, isLarge: boolean) => {
    const { route, tutorialUrl } = ELEMENT_CONFIG[element];
    const className = isLarge ? 'text-base' : 'h-auto py-2 text-sm';
    const label = t(`conversionRecommendations.elements.${element}.cta`);

    // Le tuto s'ouvre à côté : la modale reste disponible pour les autres éléments manquants.
    if (tutorialUrl) {
      return (
        <Button asChild className={className}>
          <a href={tutorialUrl} target="_blank" rel="noopener noreferrer">
            <VideoIcon />
            {label}
          </a>
        </Button>
      );
    }

    return (
      <Button
        className={className}
        onClick={() => {
          close();
          navigate(route(recommendation.webinarId));
        }}
      >
        {label}
      </Button>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) close();
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        className={`gap-8 rounded-[20px] px-6 pb-6 pt-8 ${isSingleElement ? 'max-w-[752px]' : 'max-w-[952px]'}`}
      >
        <div className="flex flex-col gap-6">
          <DialogTitle className="pb-0 text-[32px] leading-10 tracking-[-0.2px]">
            {t(`conversionRecommendations.title.${timing}`)}
          </DialogTitle>

          <div className="flex flex-col gap-[18px] text-sm leading-[18px] text-secondary">
            <p>
              {t('conversionRecommendations.intro.line1')}
              <span className="font-medium text-dark">
                {t('conversionRecommendations.intro.line1Highlight')}
              </span>
            </p>
            <p>
              {t('conversionRecommendations.intro.line2')}
              <span className="font-medium text-dark">
                {t('conversionRecommendations.intro.line2Highlight')}
              </span>
              {t('conversionRecommendations.intro.line2End')}
            </p>
            <p>{t('conversionRecommendations.intro.line3')}</p>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          {missingElements.map((element) => {
            const warnings = toStringList(
              t(`conversionRecommendations.elements.${element}.warnings`, { returnObjects: true }),
            );
            const bullets = toStringList(
              t(`conversionRecommendations.elements.${element}.bullets`, { returnObjects: true }),
            );
            const lead = t(`conversionRecommendations.elements.${element}.lead`);
            const titleAfter = t(`conversionRecommendations.elements.${element}.titleAfter`);

            return (
              <div key={element} className="flex gap-4 rounded-2xl border border-input p-4">
                <img
                  src={ELEMENT_CONFIG[element].image}
                  alt=""
                  className="w-[129px] shrink-0 self-stretch rounded-lg object-cover"
                />

                <div className="flex min-w-0 flex-1 flex-col items-end gap-4">
                  <div className="flex w-full flex-col gap-2">
                    <p className="text-xl font-medium text-dark">
                      {t(`conversionRecommendations.elements.${element}.titleBefore`)}
                      <span className="text-highlighted">
                        {t(`conversionRecommendations.elements.${element}.titleHighlight`)}
                      </span>
                      {titleAfter}
                    </p>

                    <p className="text-sm leading-[18px] text-secondary">
                      {t(`conversionRecommendations.elements.${element}.why`)}
                    </p>
                    {lead && <p className="text-sm leading-[18px] text-secondary">{lead}</p>}

                    <div className="flex flex-col gap-2">
                      {warnings.map((warning) => (
                        <div key={warning} className="flex items-start gap-2">
                          <span className="flex size-6 shrink-0 items-center justify-center">
                            <X className="size-[13.5px] text-[#F1361D]" />
                          </span>
                          <p className="text-sm leading-[18px] text-secondary">{warning}</p>
                        </div>
                      ))}
                      {bullets.map((bullet) => (
                        <div key={bullet} className="flex items-start gap-2">
                          <span className="flex size-6 shrink-0 items-center justify-center">
                            <Check className="size-[13.5px] text-[#3EB51E]" />
                          </span>
                          <p className="text-sm leading-[18px] text-secondary">{bullet}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {!isSingleElement && renderCta(element, false)}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col items-center gap-8">
          {isSingleElement && renderCta(missingElements[0], true)}

          <label className="flex w-full cursor-pointer items-center gap-2">
            <Checkbox
              checked={isDisabledPreference}
              onCheckedChange={(checked) => {
                handlePreferenceChange(checked === true);
              }}
            />
            <span className="text-sm leading-[18px] text-dark">
              {t('conversionRecommendations.dismiss')}
            </span>
          </label>
        </div>
      </DialogContent>
    </Dialog>
  );
}
